<template>
  <WdsStack v-if="fields.length > 0" direction-column class="tw-rounded">
    <HirePageSectionHeader>
      <template #title>
        <WdsHeading type="title3" class="tw-pl-2">
          {{ $t('Additional information') }}
        </WdsHeading>
      </template>
    </HirePageSectionHeader>
    <HireCustomFieldsGroup
      class="tw-px-2"
      data-test="hire-worker-custom-fields"
      :title="$t('Questions from :company', { company: companyName })"
      :sections="[{ key: 'questions', fields, values }]"
      :editable="canAnswer"
      edit-data-test="hire-worker-custom-fields-edit"
      @edit="modalOpen = true"
    />
  </WdsStack>

  <HireWorkerCustomFieldsModal v-model:open="modalOpen" :hire-id="hireId" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsHeading, WdsStack } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import HireWorkerCustomFieldsModal from '@/components/Hire/HirePageCompliance/modals/HireWorkerCustomFieldsModal.vue'
import HireCustomFieldsGroup from '@/components/Hire/HirePageParts/HireCustomFieldsGroup.vue'
import HirePageSectionHeader from '@/components/Hire/HirePageParts/HirePageSectionHeader.vue'

const props = defineProps<{ hireId: string }>()

const modalOpen = ref(false)

const { result } = useQuery(
  graphql(`
    query HireWorkerCustomFields($id: ID!, $viewerAccount: ID!) {
      hire(id: $id) {
        id
        company {
          id
          name
        }
        workerEditableCustomFields {
          ...FieldableFormCustomField
        }
        customFieldValues {
          ...CustomFieldValuesView
        }
      }
      hireInvitation(id: $id) {
        hireId
        answerableCustomFields {
          id
        }
      }
    }
  `),
  () => ({ id: props.hireId, viewerAccount: currentAccount.globalId }),
  { clientId: 'publicApi' },
)

const fields = computed(() => result.value?.hire?.workerEditableCustomFields ?? [])
const values = computed(() => result.value?.hire?.customFieldValues ?? [])
const companyName = computed(() => result.value?.hire?.company?.name ?? '')

// Answers lock once their stage has passed, so editing is only offered while some are still open.
const canAnswer = computed(
  () => (result.value?.hireInvitation?.answerableCustomFields.length ?? 0) > 0,
)
</script>
